/**
 * The shape of a page's figures row, chosen by route.
 *
 * The router's `Suspense` fallback renders before the lazy page exists, so it
 * cannot ask the page what shape it is — it only knows the path. `PageSkeleton`
 * uses the same function, so the router's placeholder and the page's own
 * data-loading placeholder always agree. Two lists would drift, and the drift
 * is visible: a reload of `/inventory` once showed the card-grid fallback, then
 * the page's strip skeleton 68ms later, then the data — two loading screens of
 * different shapes, which is the part a reader notices.
 *
 * **The default is `none`, not `cards`.** Most pages have no figures row at
 * all, and a placeholder that draws one promises content that never arrives —
 * the same shift as omitting one, but on every page that has nothing to show.
 * A route with figures says so here.
 *
 * It is hand-maintained and will drift if a page changes shape without touching
 * this file. That is the cost of the router needing an answer before the page
 * can give one. The failure is cosmetic and looks exactly like the bug it was
 * added to fix, which at least makes it easy to spot.
 */

export type PageSkeletonVariant = "cards" | "strip" | "list" | "none";

/** The shape a route's skeleton takes: its figures row, and how many figures. */
export interface PageSkeletonShape {
  variant: PageSkeletonVariant;
  /** Cards or strip cells in the figures row. Only meaningful off `none`. */
  stats: number;
  /**
   * Whether the page has a title-and-subtitle header to stand in for. False
   * for the two pages whose top is something else: a filter bar, or the
   * admin console's columns.
   */
  header: boolean;
}

const DEFAULT_STATS = 4;

/*
 * One row per route, read by the router's fallback and by the page's own
 * loading state alike, so the two draw the same figures row. A page whose
 * count differed from its entry used to pass its own `stats`, which made the
 * fallback and the page disagree by exactly that many cells on every first
 * visit; the count lives here now, and nowhere else.
 */

/**
 * Longest match wins, so a specific child can disagree with its parent.
 * `/costs` renders no figures row while `/costs/breakdown` renders a strip.
 */
const ROUTE_SHAPES: ReadonlyArray<
  readonly [string, PageSkeletonVariant, number?, boolean?]
> = [
  // One joined container with hairline dividers.
  ["/", "strip"], // DashboardSummary
  ["/inventory", "strip", 6], // a Card split by divide-x, six cells
  // One resource at full width: a detail page, no figures row.
  ["/inventory/resources", "none"],
  // The queue opens on a row of quick-filter pills; the portfolio opens on a
  // table. Neither is a metric strip any more.
  ["/desk", "none"],
  ["/desk/automation", "none"],
  ["/desk/campaigns", "list"],
  ["/desk/escalations", "list"],
  ["/desk/my-work", "list"],
  ["/desk/overdue", "list"],
  ["/desk/reports", "none"],
  ["/desk/teams", "list"],
  ["/desk/tickets", "none"],
  ["/costs/explorer", "strip", 2],
  // One service at full width: a detail page, like a resource.
  ["/costs/services", "none"],
  ["/admin/platform-analytics", "strip"],
  ["/admin/distributors/analytics", "strip"],
  ["/admin/msps/analytics", "strip"],
  ["/admin/companies/analytics", "strip"],
  ["/admin/organizations/distributors/analytics", "strip"],
  ["/admin/organizations/msps/analytics", "strip"],
  ["/admin/organizations/companies/analytics", "strip"],

  // Separated stat tiles.
  ["/desk/runs", "list"],
  ["/credentials", "list"],
  ["/alerts", "list"],
  ["/admin/organizations", "none", undefined, false], // Miller columns, no header
  ["/capabilities", "cards"],
  ["/admin", "cards"], // AdminPage's own overview tiles

  // Everything else has no figures row and inherits the default.
];

export function pageSkeletonShapeFor(pathname: string): PageSkeletonShape {
  let best: PageSkeletonShape = {
    variant: "none",
    stats: DEFAULT_STATS,
    header: true,
  };
  let bestLength = -1;
  for (const [route, variant, stats, header] of ROUTE_SHAPES) {
    const matches =
      route === "/"
        ? pathname === "/"
        : pathname === route || pathname.startsWith(`${route}/`);
    if (matches && route.length > bestLength) {
      best = { variant, stats: stats ?? DEFAULT_STATS, header: header ?? true };
      bestLength = route.length;
    }
  }
  return best;
}

export function pageSkeletonVariantFor(pathname: string): PageSkeletonVariant {
  return pageSkeletonShapeFor(pathname).variant;
}
